import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { AttendanceMonthDay, AttendanceSessionInfo } from '../../types';
import {
  ATTENDANCE_STATUS_COLOR,
  ATTENDANCE_STATUS_ICON,
  buildSessionRows,
  formatDayHeading,
  sittingsSummary,
} from '../../utils/attendanceSessions';

interface Props {
  /** "Today", or omitted to use the date itself as the heading. */
  title?: string;
  /** The calendar day shown, "2026-10-09". */
  dateKey: string;
  /** The day's record, or null when nothing was recorded. */
  day: AttendanceMonthDay | null;
  /** The registers the school takes - used to show today's untaken ones. */
  expectedSessions?: AttendanceSessionInfo[];
  isToday?: boolean;
}

/**
 * One day's attendance, register by register: the day's overall status and,
 * underneath, "Morning - Present / Afternoon - Absent" with arrival and marking
 * times in the parent's own timezone.
 */
export function DayAttendanceDetail({ title, dateKey, day, expectedSessions = [], isToday = false }: Props) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const rows = buildSessionRows(day, expectedSessions, isToday);
  const summary = sittingsSummary(day);
  const dayColor = day ? ATTENDANCE_STATUS_COLOR[day.status] : colors.textMuted;

  return (
    <View>
      <View style={styles.headerRow}>
        <View style={{ flex: 1 }}>
          {title ? <Text style={styles.title}>{title}</Text> : null}
          <Text style={title ? styles.dateText : styles.title}>{formatDayHeading(dateKey)}</Text>
        </View>
        {day ? (
          <View style={[styles.dayBadge, { backgroundColor: `${dayColor}1A`, borderColor: dayColor }]}>
            <FeatherIcon name={ATTENDANCE_STATUS_ICON[day.status]} size={14} color={dayColor} />
            <Text style={[styles.dayBadgeText, { color: dayColor }]}>{day.status}</Text>
          </View>
        ) : null}
      </View>

      {summary ? <Text style={styles.summaryText}>{summary}</Text> : null}

      {rows.length > 0 ? (
        <View style={styles.sessionList}>
          {rows.map((row, index) => {
            const color = row.status ? ATTENDANCE_STATUS_COLOR[row.status] : colors.textMuted;
            return (
              <View
                key={row.code}
                style={[styles.sessionRow, index < rows.length - 1 && styles.sessionRowDivider]}
              >
                <View style={[styles.sessionIcon, { backgroundColor: `${color}1A` }]}>
                  <FeatherIcon
                    name={row.status ? ATTENDANCE_STATUS_ICON[row.status] : 'more-horizontal'}
                    size={16}
                    color={color}
                  />
                </View>
                <View style={styles.sessionTextWrap}>
                  <Text style={styles.sessionLabel}>{row.label}</Text>
                  {row.detail ? <Text style={styles.sessionDetail}>{row.detail}</Text> : null}
                </View>
                <Text style={[styles.sessionStatus, { color }]}>{row.status ?? 'Pending'}</Text>
              </View>
            );
          })}
        </View>
      ) : (
        <Text style={styles.emptyText}>
          {day
            ? day.status === 'Leave'
              ? 'On approved leave.'
              : ''
            : isToday
              ? 'Not marked yet for today.'
              : 'No attendance was recorded for this day.'}
        </Text>
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    headerRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 12,
    },
    title: {
      fontSize: 15,
      fontWeight: '800',
      color: colors.textPrimary,
    },
    dateText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: '600',
      marginTop: 4,
    },
    dayBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      borderWidth: 1,
      borderRadius: 999,
      paddingVertical: 6,
      paddingHorizontal: 12,
    },
    dayBadgeText: {
      fontSize: 13,
      fontWeight: '800',
    },
    summaryText: {
      fontSize: 13,
      color: colors.textSecondary,
      fontWeight: '600',
      marginTop: 10,
    },
    sessionList: {
      marginTop: 14,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 14,
      paddingHorizontal: 14,
    },
    sessionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
    },
    sessionRowDivider: {
      borderBottomWidth: 1,
      borderBottomColor: colors.divider,
    },
    sessionIcon: {
      width: 34,
      height: 34,
      borderRadius: 17,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
    },
    sessionTextWrap: {
      flex: 1,
    },
    sessionLabel: {
      fontSize: 14,
      fontWeight: '700',
      color: colors.textPrimary,
    },
    sessionDetail: {
      fontSize: 12,
      color: colors.textMuted,
      fontWeight: '500',
      marginTop: 2,
    },
    sessionStatus: {
      fontSize: 13,
      fontWeight: '800',
    },
    emptyText: {
      fontSize: 14,
      color: colors.textMuted,
      fontWeight: '500',
      marginTop: 14,
    },
  });
